/**
 * HintBadge — знак «?» у кружечку з popup при кліку/наведенні.
 *
 * Reusable для довідкових підказок на сторінках довідників.
 * На відміну від нативного `title` (тривала затримка, не працює на mobile) —
 * показує popup одразу при hover, тримає при mouseenter→popup, закривається
 * на click outside.
 */
import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Текст підказки. Може містити \n для переносу рядків. */
  text: string;
  /** Заголовок попапу. Опційно. */
  title?: string;
  /** Розмір кружечка. Default 18px. */
  size?: number;
}

export default function HintBadge({ text, title = 'Довідка', size = 18 }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  // Закриваємо при click outside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseEnter={() => setOpen(true)}
        aria-label="Підказка"
        style={{
          width: size,
          height: size,
          minWidth: size,
          borderRadius: '50%',
          background: 'var(--primary)',
          color: 'white',
          border: 'none',
          fontSize: Math.round(size * 0.65),
          fontWeight: 700,
          cursor: 'help',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          lineHeight: 1,
          userSelect: 'none',
        }}
      >
        ?
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          style={{
            position: 'absolute',
            top: size + 8,
            left: 0,
            minWidth: 320,
            maxWidth: 480,
            background: 'var(--bg-panel, #fff)',
            color: 'var(--text, #1e293b)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 'var(--radius, 8px)',
            boxShadow: 'var(--shadow-lg, 0 10px 25px rgba(0,0,0,0.15))',
            padding: '0.75rem 1rem',
            zIndex: 200,
            fontSize: '0.85rem',
            lineHeight: 1.5,
            fontWeight: 400,
            whiteSpace: 'pre-line',
          }}
          role="tooltip"
        >
          <div
            style={{
              fontWeight: 700,
              marginBottom: '0.4rem',
              borderBottom: '1px solid var(--border-soft, #f1f5f9)',
              paddingBottom: '0.3rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>💡 {title}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                color: 'var(--text-secondary, #475569)',
                padding: 0,
                marginLeft: '0.5rem',
              }}
              aria-label="Закрити"
            >
              ✕
            </button>
          </div>
          {text}
        </div>
      )}
    </span>
  );
}
